import Bugsnag from "@bugsnag/js";
import { isExpired, decodeToken } from "react-jwt";
import { getCart } from "../utils";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import {
  getCookie,
  hasCookiePermission,
} from "../../layout/cookies/CookieFunctions";
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const AUTH_CLIENT_ID = getEnvironmentVariable("AUTH_CLIENT_ID");
const AUTH_TYPE = getEnvironmentVariable("AUTH_TYPE");
const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";
const bypassPage = () => {
  return window.location.pathname.toLowerCase().includes("oauth2/idresponse");
};

class AuthObject {
  constructor(initialValues) {
    this.storageType = AUTH_TYPE || "localstorage";

    // cookie consent
    const cookieVal = getCookie("CookieConsent");
    this.cookieConsent = cookieVal
      ? JSON.parse(cookieVal)
      : {
          essential: true,
          analytical: false,
          performance: false,
          informational: false,
        };

    // shopping cart
    this.cart = getCart();

    // initial values
    if (initialValues) {
      let keys = Object.keys(initialValues);
      for (let key of keys) {
        this[key] = initialValues[key];
      }
    }
  }

  initialToken() {
    if (bypassPage()) {
      return;
    }
    if (this.storageType === "localstorage") {
      this.lastAuthorisedUser = localStorage?.getItem(
        `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.LastAuthUser`
      );
      if (!this.lastAuthorisedUser) {
        this.isAuthenticated = false;
        // not logged in
        return;
      }

      this.authorisedUser = this.lastAuthorisedUser;
      this.idTokenString = localStorage?.getItem(
        `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.idToken`
      );
      this.accessTokenString = localStorage?.getItem(
        `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.accessToken`
      );
      this.refreshTokenString = localStorage?.getItem(
        `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.refreshToken`
      );
    }

    this.decodeTokens();
  }

  storeTokens() {
    if (bypassPage()) {
      return;
    }
    if (!this.authorisedUser) {
      this.removeTokens();
      return;
    }
    if (this.storageType === "localstorage") {
      if (!this.idTokenString || !this.accessTokenString) {
        this.removeTokens();
        return;
      }

      try {
        localStorage.setItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.LastAuthUser`,
          this.authorisedUser
        );
        localStorage.setItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.idToken`,
          this.idTokenString
        );
        localStorage.setItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.accessToken`,
          this.accessTokenString
        );
        localStorage.setItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.refreshToken`,
          this.refreshTokenString
        );
      } catch (err) {}
    }
  }

  decodeTokens() {
    if (bypassPage()) {
      return;
    }
    if (!this.idTokenString || !this.accessTokenString) {
      this.removeTokens();
      return;
    }

    this.identity = decodeToken(this.idTokenString);
    this.idTokenExpired = this.identity ? isExpired(this.idTokenString) : false;

    this.accessTokenDecoded = decodeToken(this.accessTokenString);
    this.accessTokenExpired = this.accessTokenDecoded
      ? isExpired(this.accessTokenString)
      : false;

    if (this.identity?.bcpEmail) {
      this.identity.email = this.identity.bcpEmail;
    }

    this.authorisedUser =
      this.identity?.preferred_username || this.identity?.email;
    this.lastAuthorisedUser =
      this.identity?.preferred_username || this.identity?.email;
    this.isAuthenticated = true; // should probably check expiry

    if (this.identity) {
      if (!this.identity?.firstName) {
        this.identity.firstName = this.identity?.given_name;
      }
      if (!this.identity?.lastName) {
        this.identity.lastName = this.identity?.family_name;
      }
      let keys = Object.keys(this.identity);
      for (let key of keys) {
        if (key.startsWith("custom:")) {
          this.identity[key.replace("custom:", "")] = this.identity[key];
          delete this.identity[key];
        }
      }

      this.identity.isAdmin =
        this.identity["cognito:groups"]?.length &&
        this.identity["cognito:groups"].includes("admin");
      if (COMPANY_FLAG === "solforge") {
        this.identity.isSFAdmin =
          this.identity["cognito:groups"]?.length &&
          this.identity["cognito:groups"].includes("Admins");
        this.identity.isSFEditor =
          this.identity["cognito:groups"]?.length &&
          this.identity["cognito:groups"].includes("Editors");
      }
      this.identity.storeUser = this.identity?.storeUser === "true";
      this.identity.subscriber =
        NO_SUBSCRIPTIONS ||
        COMPANY_FLAG === "solforge" ||
        this.identity?.subscriber === "true";
      this.identity.isOrganizer = this.identity?.isOrganizer === "true";
      this.identity.isJudge = this.identity?.isJudge === "true";
      this.identity.isTester =
        this.identity?.isTester === true || this.identity?.isTester === "true";

      if (this.identity.adminGameSystems) {
        try {
          this.identity.adminGameSystems = JSON.parse(
            this.identity.adminGameSystems
          );
        } catch (err) {}
      }
      if (this.identity.adminEnvironments) {
        try {
          this.identity.adminEnvironments = JSON.parse(
            this.identity.adminEnvironments
          );
          if (
            !this.identity.isAdmin &&
            COMPANY_FLAG &&
            this.identity.adminEnvironments.includes(COMPANY_FLAG)
          ) {
            this.identity.isAdmin = true;
          }
        } catch (err) {}
      }
      if (this.identity.federatedIdentities) {
        try {
          this.identity.federatedIdentities = JSON.parse(
            this.identity.federatedIdentities
          );

          if (IS_BCP) {
            this.identity.hasMyWarhammer =
              this.identity?.federatedIdentities?.filter(
                (el) => el.providerName === "GamesWorkshop"
              )?.length > 0;
          }
        } catch (err) {}
      }

      if (hasCookiePermission("bugSnag") && Bugsnag?._client) {
        Bugsnag.setUser(
          this.identity?.userId,
          this.identity?.email,
          `${this.identity?.firstName || this.identity?.given_name} ${
            this.identity?.lastName || this.identity?.family_name
          }`.trim()
        );
      }
    }

    return;
  }

  syncTokens(tokens) {
    if (bypassPage()) {
      return;
    }
    if (tokens) {
      this.idTokenString = tokens?.idTokenString;
      this.accessTokenString = tokens?.accessTokenString;
      this.refreshTokenString =
        tokens?.refreshTokenString || this?.refreshTokenString;

      this.decodeTokens();
      this.storeTokens();
    } else {
      this.initialToken();
    }
  }

  removeTokens() {
    if (bypassPage()) {
      return;
    }
    if (this.storageType === "localstorage") {
      try {
        localStorage.removeItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.idToken`
        );
        localStorage.removeItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.accessToken`
        );
        localStorage.removeItem(
          `AuthIdentityServiceProvider.${AUTH_CLIENT_ID}.refreshToken`
        );
      } catch (err) {}
    }
    this.idTokenString = undefined;
    this.identity = undefined;
    this.idTokenExpired = undefined;

    this.accessTokenString = undefined;
    this.accessTokenDecoded = undefined;
    this.accessTokenExpired = undefined;

    this.refreshTokenString = undefined;

    this.authorisedUser = undefined;
    this.isAuthenticated = false;
  }
}

export default AuthObject;
